import {
  isDigitalWellBeingUrl,
  isDigitalWellBeingPath,
  isK5Url,
  isK5Dashboard,
  isMiddleSchoolUrl,
  isMiddleSchoolDashboard,
  isHighSchoolUrl,
} from 'utils/productHelpers'
import { isHighSchoolDashboard } from 'utils/highschoolHelpers'

import { SITE_QUERY_PARAM } from './constants'

export const getDefaultSite = siteList => {
  if (siteList?.length === 0 || !Array.isArray(siteList)) {
    return null
  }

  const sortedSites = [...siteList].sort((a, b) => {
    const aName = a?.name || a?.siteName || ''
    const bName = b?.name || b?.siteName || ''
    return aName.localeCompare(bName)
  })

  const firstSite = sortedSites[0]

  const siteId = firstSite?.id || firstSite?.siteId || ''
  const siteName = firstSite?.name || firstSite?.siteName || ''

  return { siteId, siteName }
}

export const getSiteFromUrl = siteList => {
  // Handle test environment where window.location might be mocked
  try {
    const queryParams = new URLSearchParams(location?.search || '')
    const siteId = queryParams.get(SITE_QUERY_PARAM)
    return findSiteById(siteList, siteId)
  } catch (error) {
    // In case of errors in test environment, return null
    return null
  }
}

export const findSiteById = (siteList, searchId) => {
  if (!siteList || siteList.length === 0 || !searchId) {
    return null
  }

  // Handle test environment where site objects might have siteId instead of id
  const matchingSite = siteList?.find(
    site =>
      (site?.id && site?.id === parseInt(searchId)) ||
      (site?.siteId && site?.siteId === parseInt(searchId)),
  )

  // If no matching site is found, return null
  if (!matchingSite) return null

  // Handle test environment where site objects might have different structure
  const siteId = matchingSite?.id || matchingSite?.siteId
  const siteName = matchingSite?.name || matchingSite?.siteName || ''
  const sitePreferences = matchingSite?.sitePreferences || {}

  if (!siteId) return null

  // Check setup status for all program types with the correct program names
  const highSchoolObj = sitePreferences?.highSchool

  // Determine if the site is setup if its a high school site, otherwise default to true
  const isSetup =
    highSchoolObj?.isSetup === undefined ? true : highSchoolObj?.isSetup

  return {
    siteId,
    siteName,
    isSetup: !!isSetup,
    programPreferences: {
      highSchool: highSchoolObj?.isSetup || false,
    },
  }
}

export const isSiteQueryParamPresent = () => {
  try {
    const queryParams = new URLSearchParams(location?.search || '')
    return queryParams.has(SITE_QUERY_PARAM)
  } catch (error) {
    // In case of errors in test environment, return false
    return false
  }
}

// Helper function to determine current program type
export const getProgramType = () => {
  try {
    // DWB teach paths resolve to k5/middleSchool/highSchool via productHelpers,
    // so check the DWB route before other program URL helpers.
    if (isDigitalWellBeingUrl()) {
      return 'dwb'
    }
    if (isHighSchoolDashboard() || isHighSchoolUrl()) {
      return 'highSchool'
    }
    if (isMiddleSchoolDashboard() || isMiddleSchoolUrl()) {
      return 'middleSchool'
    }
    if (isK5Dashboard() || isK5Url()) {
      return 'k5'
    }

    return null
  } catch (error) {
    console.error('Error determining program type:', error)
    return null
  }
}

// Helper function to navigate to a new URL
export const navigate = url => {
  try {
    window.location.href = url.toString()
  } catch (error) {
    console.error('Navigation error:', error)
  }
}

// Helper function to get the active site ID key for the current program type
export const getActiveSiteIdKeyForProgramType = programType => {
  // DWB teach URLs resolve to k5/middleSchool/highSchool in productHelpers, so
  // pathname must win over programType. userContext stores digitalWellBeingActiveSiteId.
  if (isDigitalWellBeingPath(location?.pathname)) {
    return 'digitalWellBeingActiveSiteId'
  }

  switch (programType) {
    case 'highSchool':
      return 'hsActiveSiteId'
    case 'middleSchool':
      return 'middleSchoolActiveSiteId'
    case 'k5':
      return 'elementaryActiveSiteId'
    default:
      return null
  }
}

// Function to check if a site is set up
export const isSiteSetup = (site, programType, userContext) => {
  if (!site) return false

  // For Middle School and K5, we don't use the isSetup flag
  // Those programs are always considered "set up" if they exist
  if (
    programType === 'middleSchool' ||
    programType === 'k5' ||
    programType === 'dwb'
  ) {
    return true
  }

  // For High School, we need to check the isSetup flag
  if (programType === 'highSchool') {
    // First, check if the site already has programPreferences set
    if (site?.programPreferences?.highSchool) {
      return Boolean(site?.programPreferences?.highSchool)
    }

    // Fallback to checking the user context sites data
    if (!userContext?.sites?.length) return false

    const siteInContext = userContext?.sites?.find(
      s => s?.siteId === site?.siteId,
    )
    if (!siteInContext || !siteInContext?.programs?.highSchool) return false

    return Boolean(siteInContext?.programs?.highSchool?.preferences?.isSetup)
  }

  // If no specific program type is detected, assume the site is set up
  return true
}

// Function to get site and user preferences
export const getSiteAndUserPreferences = (site, allSites, programType) => {
  if (!site) return site

  // Handle case where allSites might be null or empty
  if (!allSites || !Array.isArray(allSites) || allSites.length === 0) {
    return site
  }

  // Find the site in allSites, handling both id and siteId
  const selectedSite = allSites?.find(
    s => site?.siteId === s?.id || site?.siteId === s?.siteId,
  )

  if (!selectedSite) {
    return site
  }

  const updatedSite = { ...site }
  updatedSite.claims = selectedSite?.claims || []

  // Set program-specific preferences based on program type
  switch (programType) {
    case 'highSchool':
      updatedSite.highSchoolSitePreferences =
        selectedSite?.sitePreferences?.highSchool || {}
      updatedSite.highSchoolEducatorPreferences = {
        ...(selectedSite?.sitePreferences?.highSchool?.userProgramPreferences ||
          {}),
      }
      break
    case 'middleSchool':
      updatedSite.middleSchoolSitePreferences =
        selectedSite?.sitePreferences?.mspDigital || {}
      updatedSite.middleSchoolEducatorPreferences = {
        ...(selectedSite?.sitePreferences?.mspDigital?.userProgramPreferences ||
          {}),
      }
      break
    case 'k5':
      updatedSite.k5SitePreferences =
        selectedSite?.sitePreferences?.k5Digital || {}
      updatedSite.k5EducatorPreferences = {
        ...(selectedSite?.sitePreferences?.k5Digital?.userProgramPreferences ||
          {}),
      }
      break
    default:
      // No default preferences
      break
  }

  return updatedSite
}
